/**
 * User Measurement Types
 *
 * Type definitions for user body measurements including weight, height,
 * body fat percentage, and other physical metrics. These types support
 * measurement logging, tracking, and image attachments.
 *
 * @module types/domain/user-measurement
 */

import type { QueryParams } from '../api/query.types';
import type { ID } from '../common/id.types';

// =============================================================================
// BASIC TYPE ALIASES
// =============================================================================

/**
 * Keys for body measurement metrics.
 *
 * Represents the available measurement types that can be tracked.
 *
 * @example
 * ```ts
 * const measurementType: BodyMeasurementKey = 'weight';
 * const anotherType: BodyMeasurementKey = 'body_fat_percentage';
 * ```
 */
export type BodyMeasurementKey =
  | 'weight'
  | 'height'
  | 'body_fat_percentage'
  | 'body_mass_index'
  | 'muscle_mass'
  | 'bone_mass'
  | 'water_percentage'
  | 'visceral_fat'
  | 'lean_body_mass'
  | 'fat_free_mass'
  | 'skeletal_muscle_mass'
  | 'body_fat_mass'
  | 'metabolic_age'
  | 'basal_metabolic_rate_bmr'
  | 'daily_calorie_needs'
  | 'waist_circumference'
  | 'hip_circumference'
  | 'chest_circumference'
  | 'neck_circumference'
  | 'thigh_circumference'
  | 'calf_circumference'
  | 'upper_arm_circumference'
  | 'bicep_circumference'
  | 'forearm_circumference'
  | 'shoulder_circumference'
  | 'ankle_circumference'
  | 'wrist_circumference'
  | 'resting_heart_rate'
  | 'blood_pressure_systolic'
  | 'blood_pressure_diastolic'
  | 'vo2_max'
  | 'recovery_heart_rate'
  | 'heart_rate_variability_hrv'
  | 'grip_strength'
  | 'flexibility_score'
  | 'balance_score'
  | 'vertical_jump'
  | 'push_ups_max'
  | 'plank_hold_time'
  | 'mile_run_time'
  | 'sprint_time'
  | 'flexibility_sit_and_reach'
  | 'steps_per_day'
  | 'active_minutes'
  | 'sleep_quality_score'
  | 'energy_level'
  | 'stress_level'
  | 'mood_score'
  | 'blood_glucose'
  | 'body_temperature';

/**
 * All available body measurement keys as an array.
 * Useful for validation and iteration.
 */
export const BODY_MEASUREMENT_KEYS: readonly BodyMeasurementKey[] = [
  'weight',
  'height',
  'body_fat_percentage',
  'body_mass_index',
  'muscle_mass',
  'bone_mass',
  'water_percentage',
  'visceral_fat',
  'lean_body_mass',
  'fat_free_mass',
  'skeletal_muscle_mass',
  'body_fat_mass',
  'metabolic_age',
  'basal_metabolic_rate_bmr',
  'daily_calorie_needs',
  'waist_circumference',
  'hip_circumference',
  'chest_circumference',
  'neck_circumference',
  'thigh_circumference',
  'calf_circumference',
  'upper_arm_circumference',
  'bicep_circumference',
  'forearm_circumference',
  'shoulder_circumference',
  'ankle_circumference',
  'wrist_circumference',
  'resting_heart_rate',
  'blood_pressure_systolic',
  'blood_pressure_diastolic',
  'vo2_max',
  'recovery_heart_rate',
  'heart_rate_variability_hrv',
  'grip_strength',
  'flexibility_score',
  'balance_score',
  'vertical_jump',
  'push_ups_max',
  'plank_hold_time',
  'mile_run_time',
  'sprint_time',
  'flexibility_sit_and_reach',
  'steps_per_day',
  'active_minutes',
  'sleep_quality_score',
  'energy_level',
  'stress_level',
  'mood_score',
  'blood_glucose',
  'body_temperature',
] as const;

// =============================================================================
// QUERY PARAMETERS
// =============================================================================

/**
 * Query parameters for fetching user measurements.
 *
 * Extends the base QueryParams with measurement-specific filtering.
 *
 * @example
 * ```ts
 * const params: UserMeasurementQueryParams = {
 *   measurement: 'weight',
 *   page: 1,
 *   limit: 10,
 * };
 * ```
 */
export interface UserMeasurementQueryParams extends QueryParams {
  /** Filter by specific measurement type; the answer is that metric, flattened */
  measurement?: BodyMeasurementKey;
}

// =============================================================================
// MEASUREMENT INTERFACES
// =============================================================================

/**
 * Collection of body measurement metric values.
 *
 * All values are optional and nullable, representing measurements
 * that may or may not be tracked for a given log entry.
 *
 * @example
 * ```ts
 * const metrics: UserMeasurementMetrics = {
 *   weight: 75.5,
 *   body_fat_percentage: 18.2,
 *   height: null, // Not measured this time
 * };
 * ```
 */
export type UserMeasurementMetrics = Partial<
  Record<BodyMeasurementKey, number | null>
>;

/**
 * User measurements list item.
 *
 * Lightweight representation of a measurement entry used in list views.
 * Contains the logged date and all metric values directly on the object.
 *
 * @example
 * ```ts
 * const measurements: UserMeasurements[] = [
 *   {
 *     logged: '2024-01-15T10:30:00Z',
 *     weight: 75.5,
 *     body_fat_percentage: 18.2,
 *   },
 * ];
 * ```
 */
export type UserMeasurements = UserMeasurementMetrics & {
  /** Unique identifier (list responses always include it). */
  id: ID;
  /** ISO 8601 timestamp when measurement was logged */
  logged: string;
  /** Nested metrics object (optional, used in some API responses) */
  metrics?: UserMeasurementMetrics;
  /**
   * How many progress photos the VIEWER may see on this entry: 0 wherever the
   * entry's own endpoint would answer `images: []` (none, or hidden from this
   * trainer or friend). Always sent; optional because fixtures predate it.
   */
  images_count?: number;
};

/**
 * Single measurement metric with its key and value.
 *
 * Used in the detailed measurement view where metrics are stored
 * as an array of key-value pairs.
 *
 * @example
 * ```ts
 * const metric: UserMeasurementMetric = {
 *   key: 'weight',
 *   value: 75.5,
 * };
 * ```
 */
export interface UserMeasurementMetric {
  /** The measurement type */
  key: BodyMeasurementKey;
  /** The measurement value */
  value: number;
}

/**
 * Image attached to a measurement entry.
 *
 * Progress photos or other visual documentation associated with measurements.
 *
 * @example
 * ```ts
 * const image: UserMeasurementImage = {
 *   id: 123,
 *   url: 'https://example.com/images/progress-photo-123.jpg',
 * };
 * ```
 */
export interface UserMeasurementImage {
  /** Unique identifier for the image; what removing it takes. */
  id: ID;
  /**
   * A signed URL that stops working 3 minutes after the entry was fetched.
   * Fetch the entry again for a fresh one; never cache it.
   */
  url: string;
}

/**
 * Detailed user measurement entry.
 *
 * Complete measurement record with metrics as key-value pairs,
 * attached images, and timestamps.
 *
 * @example
 * ```ts
 * const measurement: UserMeasurement = {
 *   id: 456,
 *   metrics: [
 *     { key: 'weight', value: 75.5 },
 *     { key: 'body_fat_percentage', value: 18.2 },
 *   ],
 *   images: [
 *     { id: 123, url: 'https://example.com/images/photo.jpg' },
 *   ],
 *   logged: '2024-01-15T10:30:00Z',
 *   updated: '2024-01-15T10:30:00Z',
 * };
 * ```
 */
export interface UserMeasurement {
  /** Unique identifier (optional when creating) */
  id?: ID;
  /** Array of measurement metrics */
  metrics: UserMeasurementMetric[];
  /** Array of attached images */
  images: UserMeasurementImage[];
  /** ISO 8601 timestamp when measurement was logged */
  logged: string;
  /** ISO 8601 timestamp when measurement was last updated */
  updated: string;
}

// =============================================================================
// CATALOGUE + METRIC DETAIL (GET /body-measurement, GET .../metric/{slug})
// =============================================================================

/**
 * A metric family, as the backend defines it.
 *
 * These are the canonical eight from `BodyMeasurementCategory`. The web app
 * previously grouped the board by five hand-rolled sections that had no
 * relationship to these; the catalogue is now the single vocabulary.
 *
 * Only seven are ever seeded — `other` is the column default and no catalogued
 * row carries it — so group off what the catalogue returns rather than off this
 * union, or the board grows a permanently empty section.
 */
export type BodyMeasurementCategory =
  | 'basic_body_composition'
  | 'advanced_body_composition'
  | 'circumference_measurements'
  | 'cardiovascular_health'
  | 'performance_strength'
  | 'activity_lifestyle'
  | 'health_adjacent'
  | 'other';

/**
 * One catalogued metric: what it is called, what family it belongs to, and —
 * resolved for the requesting user — the unit it reads in and the bounds a
 * plausible value falls between.
 *
 * `unit` is the DISPLAY unit for the requester (`kg` or `lbs`), never the
 * canonical one, and is `null` for the metrics that do not vary by system.
 * Those keep their labels on the client — see `PASSTHROUGH_UNITS`.
 *
 * `min`/`max` arrive converted to the same unit as the readings, so they can be
 * compared directly. They are absolute plausibility bounds, and only 10 of the
 * catalogue's 50 rows carry them; the rest are `null`.
 */
export interface BodyMeasurementCatalogItem {
  slug: BodyMeasurementKey;
  name: string;
  category: BodyMeasurementCategory;
  category_label: string;
  unit: string | null;
  min: number | null;
  max: number | null;
}

/** One entry's value for a metric, plus the metrics logged alongside it. */
export interface MeasurementMetricReading {
  /** Entry hashid — routes to the edit form for that entry. */
  id: ID;
  /** ISO date (`Y-m-d`); entries are unique per day. */
  logged: string;
  /** Already in the requester's system — the client never converts. */
  value: number;
  /**
   * Sibling metric slugs from the same entry, filtered to the catalogue, so
   * every one of them resolves on this same endpoint.
   */
  with: BodyMeasurementKey[];
}

/** The active goal tracking a metric, where one exists. */
export interface MeasurementMetricGoal {
  id: ID;
  target_value: number;
  starting_value: number;
  /** Display label (`'Increase'` / `'Decrease'`), matching the goal resources. */
  goal_direction: string;
}

/** A metric worth overlaying on the series — the comparison picker's rows. */
export interface MeasurementComparableMetric {
  slug: BodyMeasurementKey;
  name: string;
  unit: string | null;
}

/**
 * One metric's whole record for a user.
 *
 * Deliberately free of statistics: rate, range, typical gap, rolling mean and
 * projection are all derived client-side so the range chips recompute without
 * refetching. Interpretation of the same series (bucketed, trend-scored,
 * correlated against training load) belongs to Reports.
 */
export interface MeasurementMetricDetail {
  metric: BodyMeasurementCatalogItem;
  /** Chronological, oldest first. */
  readings: MeasurementMetricReading[];
  goal: MeasurementMetricGoal | null;
  comparable: MeasurementComparableMetric[];
}
